import { Button } from "@somethings-ui/web";
import { Scribbles } from "@somethings-ui/core/assets";
import { PlayIcon } from "@heroicons/react/24/solid";
import { ArrowLeftIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import type { TrainingNextLevel } from "@/lib/api/training-simulator/training-simulator";
import { Dialog, DialogContent } from "@/components/ui/dialog.tsx";
import { HeartHandshakeIcon } from "lucide-react";

interface TrainingLevelCompleteModalProps {
  open: boolean;
  levelPosition: number;
  modulePosition: number;
  moduleTitle: string;
  nextLevel: TrainingNextLevel | null;
  isContinuing: boolean;
  isRetrying: boolean;
  onContinue: () => void;
  onRetry: () => void;
  onBackToTraining: () => void;
}

export function TrainingLevelCompleteModal({
  open,
  levelPosition,
  modulePosition,
  moduleTitle,
  nextLevel,
  isContinuing,
  isRetrying,
  onContinue,
  onRetry,
  onBackToTraining,
}: TrainingLevelCompleteModalProps) {
  return (
    <Dialog open={open}>
      <DialogContent
        className="items-center gap-0 overflow-hidden rounded-md bg-surfaces-z3 px-6 py-16 sm:max-w-4xl"
        hideCloseButton
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <img
          src={Scribbles.yellowSpiral}
          alt=""
          aria-hidden
          className="pointer-events-none absolute -right-48 -top-48 w-[420px] max-w-none select-none"
        />
        <img
          src={Scribbles.yellowSpiral}
          alt=""
          aria-hidden
          className="pointer-events-none absolute -bottom-48 -left-48 w-[420px] max-w-none rotate-180 select-none"
        />

        <div className="relative z-10 flex w-full flex-col items-center gap-12">
          <div className="flex flex-col items-center gap-4">
            <div className="flex size-12 items-center justify-center rounded-full bg-brand-forest">
              <HeartHandshakeIcon className="size-6 text-text-primary" />
            </div>
            <div className="mb-4 flex items-start gap-3">
              <span className="text-caption-large text-text-secondary opacity-75">
                {String(levelPosition).padStart(2, "0")}
              </span>
              <h2 className="font-heading text-[84px] font-bold leading-[0.9] tracking-[-0.03em] text-text-primary">
                Level complete
              </h2>
            </div>
            <p className="text-body-1 text-text-secondary opacity-75">
              Module {modulePosition} · {moduleTitle}
            </p>
          </div>

          <div className="flex w-full max-w-xl flex-col gap-4">
            {nextLevel && (
              <div className="flex items-center justify-between rounded-md border border-surfaces-divider bg-surfaces-z0 p-6">
                <div className="flex items-center gap-3">
                  <div className="flex size-12 items-center justify-center rounded-full bg-brand-forest">
                    <HeartHandshakeIcon className="size-5 text-text-primary" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <span className="text-body-3 text-text-secondary">
                      Level {nextLevel.position}
                    </span>
                    <span className="text-header-5 text-text-primary">
                      {nextLevel.title}
                    </span>
                    <span className="text-body-3 text-text-secondary">
                      Next up
                    </span>
                  </div>
                </div>
                <Button
                  variant="primary"
                  fullWidth={false}
                  leadingIcon={<PlayIcon className="size-4" />}
                  loading={isContinuing}
                  disabled={isRetrying}
                  onClick={onContinue}
                >
                  Continue
                </Button>
              </div>
            )}

            <div className="flex w-full gap-3">
              <div className="flex-1">
                <Button
                  variant="secondary"
                  fullWidth
                  leadingIcon={<ArrowPathIcon className="size-4" />}
                  loading={isRetrying}
                  disabled={isContinuing}
                  onClick={onRetry}
                >
                  Try this Level again
                </Button>
              </div>
              <div className="flex-1">
                <Button
                  variant="secondary"
                  fullWidth
                  leadingIcon={<ArrowLeftIcon className="size-4" />}
                  onClick={onBackToTraining}
                >
                  Back to Training Room
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
